import React, { useState, useEffect } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import DateRangePicker from '../components/DateRangePicker';
import api from '../api';
import {
  PieChart, Pie, Cell, Legend, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Area, AreaChart,
} from 'recharts';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MILESTONE_COLORS = ['#4BB0CC', '#3B65D0', '#4E87CB', '#85EBBA', '#6EC49A'];

function getDefaultDates() {
  const now = new Date();
  const end = now.toISOString().split('T')[0];
  const start = new Date(now); start.setDate(start.getDate() - 29);
  return { start: start.toISOString().split('T')[0], end };
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="portal-card" style={{ padding: '10px 14px', fontSize: '13px' }}>
        <div style={{ fontWeight: '600', marginBottom: '4px' }}>{label}</div>
        <div>Total sessions: <strong>{payload[0].value?.toLocaleString()}</strong></div>
      </div>
    );
  }
  return null;
};

export default function DashboardPage() {
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async (start, end) => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard', { params: { start, end } });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(startDate, endDate);
  }, []);

  const handleDateChange = (start, end) => {
    if (start && end) {
      setStartDate(start);
      setEndDate(end);
      fetchData(start, end);
    }
  };

  // Milestone chart data
  const milestoneData = data?.user_milestones ? [
    { name: `Quotes Started - ${data.user_milestones.quote_started}`, value: data.user_milestones.quote_started },
    { name: `Leads - ${data.user_milestones.leads_submitted}`, value: data.user_milestones.leads_submitted },
    { name: `App Requests - ${data.user_milestones.app_requests}`, value: data.user_milestones.app_requests },
  ].filter(d => d.value > 0) : [];

  // Conversion doughnut
  const convRate = parseFloat(data?.conversion_rate || 0);
  const convData = [
    { name: 'Converted', value: convRate },
    { name: 'Remaining', value: Math.max(0, 100 - convRate) },
  ];

  // Monthly sessions
  const sessionsData = MONTHS.map((month, i) => ({
    month,
    sessions: data?.sessions_by_month?.[i] || 0,
  }));

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div></div>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
          />
        </div>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
            {[1,2,3].map(i => (
              <div key={i} className="portal-card" style={{ height: '220px', padding: '20px' }}>
                <div className="skeleton" style={{ height: '20px', width: '60%', marginBottom: '16px', borderRadius: '4px' }}></div>
                <div className="skeleton" style={{ height: '140px', borderRadius: '8px' }}></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Charts grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '4fr 3fr 4fr', gap: '20px', marginBottom: '20px' }}>
              {/* User Milestones */}
              <div className="portal-card" style={{ padding: '0' }}>
                <div style={{ padding: '18px 16px', fontSize: '15px', fontWeight: '600', borderBottom: '1px solid #DDE7F1' }}>
                  User milestones
                </div>
                <div style={{ padding: '16px', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {milestoneData.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: '13px' }}>
                      No data available for this time period
                    </div>
                  ) : (
                    <PieChart width={280} height={180}>
                      <Pie
                        data={milestoneData}
                        cx={90}
                        cy={85}
                        innerRadius={55}
                        outerRadius={80}
                        dataKey="value"
                        startAngle={90}
                        endAngle={-270}
                        paddingAngle={3}
                      >
                        {milestoneData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={MILESTONE_COLORS[index % MILESTONE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Legend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                        formatter={(value) => <span style={{ fontSize: '11px', color: '#334155' }}>{value}</span>}
                        iconType="square"
                        iconSize={10}
                      />
                      <text x={90} y={85} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: '18px', fontWeight: '600', fill: '#0F172A' }}>
                        {data?.user_milestones?.total || 0}
                      </text>
                    </PieChart>
                  )}
                </div>
              </div>

              {/* Lead Conversion Rate */}
              <div className="portal-card" style={{ padding: '0' }}>
                <div style={{ padding: '18px 16px', fontSize: '15px', fontWeight: '600', borderBottom: '1px solid #DDE7F1' }}>
                  Lead Conversion Rate
                </div>
                <div style={{ padding: '16px', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  <PieChart width={160} height={160}>
                    <Pie
                      data={[{ value: 80 }]}
                      cx={80} cy={80}
                      innerRadius={50} outerRadius={65}
                      dataKey="value" startAngle={90} endAngle={-270}
                    >
                      <Cell fill="#DDE7F1" />
                    </Pie>
                    <Pie
                      data={convData}
                      cx={80} cy={80}
                      innerRadius={50} outerRadius={70}
                      dataKey="value" startAngle={90} endAngle={-270}
                    >
                      <Cell fill="#3B65D0" />
                      <Cell fill="transparent" />
                    </Pie>
                  </PieChart>
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '20px', fontWeight: '700', color: '#0F172A',
                  }}>
                    {convRate.toFixed(1)}%
                  </div>
                </div>
              </div>

              {/* Referral Sources */}
              <div className="portal-card" style={{ padding: '0' }}>
                <div style={{ padding: '18px 16px', fontSize: '15px', fontWeight: '600', borderBottom: '1px solid #DDE7F1' }}>
                  Referral sources
                </div>
                <div style={{ padding: '16px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px', marginBottom: '8px' }}>
                    <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#64748B' }}>Source</div>
                    <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#64748B' }}>Sessions</div>
                  </div>
                  {!data?.referral_sources || Object.keys(data.referral_sources).length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#94A3B8', fontSize: '12px', paddingTop: '20px' }}>No available data for this time period</p>
                  ) : (
                    Object.entries(data.referral_sources).map(([key, val]) => (
                      <div key={key} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px', padding: '4px 0', borderTop: '1px solid #F1F5F9' }}>
                        <div style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#334155' }}>{key}</div>
                        <div style={{ fontSize: '13px', color: '#334155' }}>{val}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Monthly Sessions Line Chart */}
            <div className="portal-card" style={{ padding: '0' }}>
              <div style={{ padding: '18px 16px', fontSize: '15px', fontWeight: '600', borderBottom: '1px solid #DDE7F1' }}>
                Monthly session report
              </div>
              <div style={{ padding: '20px', height: '220px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sessionsData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <defs>
                      <linearGradient id="sessionGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2D87F0" stopOpacity={0.1} />
                        <stop offset="95%" stopColor="#2D87F0" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#E9EFF6" strokeDasharray="0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone" dataKey="sessions"
                      stroke="#2D87F0" strokeWidth={2}
                      fill="url(#sessionGradient)"
                      dot={{ fill: 'white', stroke: '#4BB0CC', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: '#2D87F0' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </ProtectedLayout>
  );
}
